import { ConsumerPortalAdminConfig } from '../models/consumer-portal-admin-config';
import { consumerPortalAdminDefaults } from '../../input-config/consumer-portal-admin-config';
import { ConsumerPortalConfig, EnabledFeatures } from '../models/consumer-portal-config';
import { LoggerService } from '../services/logger.service';
import { Injectable, DOCUMENT, inject } from '@angular/core';
import isEmpty from 'lodash-es/isEmpty';
import { BehaviorSubject, catchError, map, of, switchMap } from 'rxjs';
import { AuthKeys } from '../enums/local-storage-keys.enum';
import { ConsumerPortalApiService } from './consumer-portal-api.service';
import { OrganizationConfig } from '../models/organization-config.model';
import { ErrorService } from './error.service';
import { ErrorType } from '../models/error.model';
import { AppFacadeService } from './app-facade.service';
import { FeatureToggleService } from './feature-toggle.service';
import { consumerPortalDefaults } from '../../input-config/consumer-portal-config';
import { SiteSettingService } from './site-setting.service';

import { AppMediatorService } from './app-mediator.service';
import { OrganizationType } from '../models/organization-type';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { DEFAULT_DEPOT_ID } from '../constants/default.const';
import { ComponentsToLoad } from '../enums/components-to-load.enum';
import { ApiQuery } from '../models/api-query.model';
import { Searchable } from '../models/searchable.model';

/**
 * Payment Token Extract Response Interface
 * Copied to avoid circular dependency with api-lib
 * Source: libs/consumer-portal/api-lib/src/lib/routes/apikey/payment-token/interfaces/payment-token-request.interface.ts
 */
interface PaymentTokenExtractResponse extends Searchable {
    organizationId: string;
    customerId: string;
    contractIds: string[];
    amount?: number;
    expiresAt: number;
    accessToken: string;
    refreshToken: string | null;
    email: string;
}
@Injectable({
    providedIn: 'root'
})
export class ConfigurationService {
    private readonly consumerPortalApi = inject(ConsumerPortalApiService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly logger = inject(LoggerService);
    private readonly errorService = inject(ErrorService);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly siteSetting = inject(SiteSettingService);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly title = inject(Title);
    private readonly http = inject(HttpClient);

    config: ConsumerPortalConfig | ConsumerPortalAdminConfig | undefined;

    private readonly adminInputs: BehaviorSubject<ConsumerPortalAdminConfig> = new BehaviorSubject(consumerPortalAdminDefaults);
    adminInputs$ = this.adminInputs.pipe(
        map(inputs => {
            const cachedAuthDetails = this.appMediatorService.localStorageService.getCurrentUser?.Auth;
            const newTokenInitialized = this.appMediatorService.localStorageService.getCurrentUser?.initialInputConfigAccessToken !== inputs.Auth?.AccessToken;

            // AccessToken has to be checked for backward compatibility
            let accessToken;
            if (!cachedAuthDetails?.accessToken && !inputs.Auth?.accessToken) {
                accessToken = cachedAuthDetails?.AccessToken ?? inputs.Auth?.AccessToken;
            } else {
                accessToken = cachedAuthDetails?.accessToken ?? inputs.Auth?.accessToken;
            }

            let refreshToken = cachedAuthDetails?.RefreshToken ?? inputs.Auth?.RefreshToken;
            if (newTokenInitialized) {
                accessToken = inputs.Auth?.AccessToken ?? inputs.Auth?.accessToken;
                refreshToken = inputs.Auth?.RefreshToken;
            }

            const loggedInUserConfig = {
                ...inputs,
                /* eslint-disable @typescript-eslint/naming-convention */
                /**
                 * Note camelCase : DB Model
                 */
                Auth: {
                    accessToken: accessToken,
                    RefreshToken: refreshToken,
                    // Always set InitialLogin to false in the localstorage
                    initialLogin: false,
                    OrganizationType: inputs.organizationType,
                    OrganizationId: inputs.organizationId,
                    Email: inputs.Auth?.Email
                },
                initialInputConfigAccessToken:
                    this.appMediatorService.localStorageService.getCurrentUser?.initialInputConfigAccessToken !== inputs.Auth?.AccessToken
                        ? inputs.Auth?.accessToken ?? inputs.Auth?.AccessToken ?? ''
                        : this.appMediatorService.localStorageService.getCurrentUser?.initialInputConfigAccessToken ?? '',
                newTokenInitialized: this.appMediatorService.localStorageService.getCurrentUser?.initialInputConfigAccessToken !== inputs.Auth?.AccessToken
            };

            this.appMediatorService.localStorageService.persistUser(AuthKeys.User, loggedInUserConfig);
            return loggedInUserConfig;
        }),
        switchMap(inputs => this.getOrgConfig(inputs))
    );

    private readonly orgConfiguration: BehaviorSubject<OrganizationConfig> = new BehaviorSubject<OrganizationConfig>({});
    orgConfig$ = this.orgConfiguration.asObservable();

    /**
     * Local variables moved from Consumer portal component to service
     */
    inputs: ConsumerPortalConfig | undefined; // this variable contents inputs config provided through index.html
    loginConfig!: string;
    faviconEl: HTMLLinkElement | null = this.document.querySelector('#appIcon');

    cpInputs$ = this.appFacadeService.getConfig();

    getOrgConfig(inputs: ConsumerPortalAdminConfig) {
        return this.consumerPortalApi.getPublicOrganizationConfigurations(inputs.organizationId).pipe(
            switchMap((orgConfig?: OrganizationConfig | null) => {
                let result = inputs;
                if (orgConfig) {
                    result = {
                        ...inputs,
                        styling: {
                            ...inputs?.styling,
                            themeColors: {
                                // top level theme colors
                                primaryColor: orgConfig.PrimaryColor ? orgConfig.PrimaryColor : consumerPortalAdminDefaults.styling?.themeColors?.primaryColor,
                                secondaryColor: orgConfig.SecondaryColor ? orgConfig.SecondaryColor : consumerPortalAdminDefaults.styling?.themeColors?.secondaryColor
                            },
                            // each component theme colors - apply to each component if required
                            header: {
                                ...inputs?.styling?.header,
                                primaryColor: orgConfig.PrimaryColor,
                                secondaryColor: orgConfig.SecondaryColor
                            },
                            footer: {
                                ...inputs?.styling?.footer,
                                primaryColor: orgConfig.PrimaryColor,
                                secondaryColor: orgConfig.SecondaryColor
                            }
                        },
                        header: {
                            ...inputs?.header,
                            logoUrl: orgConfig.LogoUrl ? orgConfig.LogoUrl : consumerPortalAdminDefaults.header?.logoUrl
                        }
                    };
                }

                this.orgConfiguration.next(orgConfig || {});
                return of(result);
            }),
            catchError(error => {
                this.logger.logError(error);
                this.logger.alertDevError(error);
                this.errorService.setError(ErrorType.InvalidSubDomainOrgId);
                return of(inputs);
            }),
            catchError(i => of(i)) // In case of error, return the original inputs
        );
    }

    getCpOrgConfig(inputs: ConsumerPortalConfig) {
        if (inputs.organizationId) {
            return this.consumerPortalApi.getPublicOrganizationConfigurations(inputs.organizationId).pipe(
                switchMap((orgConfig?: OrganizationConfig | null) => {
                    let result = inputs;
                    if (orgConfig) {
                        result = {
                            ...inputs,
                            depots: orgConfig.Depots, // All enabled/visible depots (Disabled=0 AND Hidden=0)
                            Locale: orgConfig.Locale, // Organization locale for date/time formatting (e.g., 'en-GB', 'en-US')
                            styling: {
                                ...inputs?.styling,
                                themeColors: {
                                    // top level theme colors
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                accountSummary: {
                                    ...inputs?.styling?.accountSummary,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                contracts: {
                                    ...inputs?.styling?.contracts,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                itemsOut: {
                                    ...inputs?.styling?.itemsOut,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                tracking: {
                                    ...inputs?.styling?.tracking,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                invoices: {
                                    ...inputs?.styling?.invoices,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                login: {
                                    ...inputs?.styling?.login,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                header: {
                                    ...inputs?.styling?.header,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                },
                                footer: {
                                    ...inputs?.styling?.footer,
                                    primaryColor: orgConfig.PrimaryColor,
                                    secondaryColor: orgConfig.SecondaryColor
                                }
                            },
                            header: {
                                ...inputs?.header,
                                logoUrl: orgConfig.LogoUrl ? orgConfig.LogoUrl : consumerPortalDefaults.header?.logoUrl,
                                faviconUrl: orgConfig.FaviconUrl,
                                title: inputs.isEmbedded ? inputs.header?.title ?? null : orgConfig.StoreName,
                                depotName: orgConfig?.Depots?.find(d => d.Id === DEFAULT_DEPOT_ID)?.Name, // Default to DEFAULT_DEPOT_ID
                                exterConsumerPortalUrl: orgConfig?.ExternalConsumerPortalURL
                            },
                            AdImageURL: {
                                ...inputs?.AdImageURL,
                                Banner1: orgConfig.Banner1,
                                Banner1ExternalUrl: orgConfig.Banner1ExternalUrl,
                                Banner2: orgConfig.Banner2,
                                Banner2ExternalUrl: orgConfig.Banner2ExternalUrl,
                                Banner3: orgConfig.Banner3,
                                Banner3ExternalUrl: orgConfig.Banner3ExternalUrl,
                                Banner4: orgConfig.Banner4,
                                Banner4ExternalUrl: orgConfig.Banner4ExternalUrl
                            },
                            payMinimumAmountDueText: orgConfig.PayMinimumAmountDueText,
                            enabledFeatures: {
                                ...inputs?.enabledFeatures,
                                [this.featureToggleService.getType() as keyof EnabledFeatures]: {
                                    ...inputs?.enabledFeatures?.[this.featureToggleService.getType() as keyof EnabledFeatures],
                                    secureUrlLoginRequired: orgConfig.SecureUrlLoginRequired,
                                    callOffAbility: orgConfig?.CallOffAbility,
                                    trackingTab: orgConfig.TrackingTab,
                                    requestService: orgConfig.RequestServiceAbility,
                                    multiplePaymentAbility: orgConfig.MultiplePaymentAbility,
                                    quotesPaymentAbility: orgConfig.QuotesPaymentAbility,
                                    enabledPrivacyPolicy: orgConfig.EnablePrivacyPolicy,
                                    liveTracking: orgConfig.LiveTracking
                                }
                            },
                            hiddenFeatures: [
                                ...(orgConfig.HiddenFeatures ?? []),
                                // AssetTags boolean false → merge into hiddenFeatures so featureToggleService blocks the route
                                ...(orgConfig.AssetTags === false ? ['AssetTags'] : []),
                                ...(orgConfig.AutoConvertQuotes === false ? ['AutoConvertQuotes'] : [])
                            ],
                            licVersion: orgConfig.version,
                            storeName: orgConfig.StoreName,
                            privacyURL: orgConfig.PrivacyURL,
                            supportEmail: orgConfig.SupportEmail
                        } as unknown as ConsumerPortalConfig;
                        if (orgConfig.DefaultPaymentMid) {
                            this.appFacadeService.setDefaultPaymentMid(orgConfig.DefaultPaymentMid);
                        }
                    }
                    return of(result);
                }),
                catchError(error => {
                    this.logger.logError(error);
                    this.logger.alertDevError(error);
                    this.errorService.setError(ErrorType.InvalidSubDomainOrgId);
                    return of(inputs);
                }),
                catchError(i => of(i)) // In case of error, return the original inputs
            );
        }
        return of(inputs);
    }

    setAdminConfigurations(adminConfig?: string, adminAppConfig?: ConsumerPortalAdminConfig) {
        if (adminConfig && !isEmpty(adminConfig)) {
            const inputConfig = JSON.parse(adminConfig) as ConsumerPortalAdminConfig;
            const consolidatedInputs: ConsumerPortalAdminConfig = {
                ...consumerPortalAdminDefaults,
                ...adminAppConfig,
                ...inputConfig
            };
            this.setGlobalHiddenFeatures(consolidatedInputs?.hiddenFeatures ?? []);
            this.config = consolidatedInputs;
            this.consumerPortalApi.setConsumerPortalApiRoute(consolidatedInputs.apiUrl);
            this.adminInputs.next(consolidatedInputs);
            return;
        }

        if (adminAppConfig) {
            this.config = adminAppConfig;
            this.consumerPortalApi.setConsumerPortalApiRoute(adminAppConfig.apiUrl);
        }
    }

    setConsumerPortalConfigurations(cpConfig?: string, cpAppConfig?: ConsumerPortalConfig) {
        // Check if payment token is present
        const parsedConfig = cpConfig ? JSON.parse(cpConfig) : {};
        const config = { ...cpAppConfig, ...parsedConfig };

        if (config.paymentToken) {
            // Handle payment token initialization asynchronously
            this.initializeFromPaymentToken(config, config.paymentToken)
                .then(modifiedConfig => {
                    const consolidatedConfig = this.consolidateConsumerPortalConfig(undefined, modifiedConfig);
                    this.appFacadeService.setConfig(consolidatedConfig);

                    // Explicitly trigger redirection (can't rely on setRedirectComponentSession which already ran)
                    this.appFacadeService.startRedirection(ComponentsToLoad.Payment, consolidatedConfig.organizationId || '', consolidatedConfig);
                })
                .catch(error => {
                    this.logger.logError(error as Error);
                    // Still set config even on error to prevent stuck "Logging in" state
                    this.appFacadeService.setConfig(this.consolidateConsumerPortalConfig(cpConfig, cpAppConfig));
                });
        } else {
            // Normal flow without payment token
            this.appFacadeService.setConfig(this.consolidateConsumerPortalConfig(cpConfig, cpAppConfig));
        }
        return;
    }

    /**
     * Initialize Consumer Portal from Payment Token
     *
     * Called when config contains a paymentToken instead of traditional Auth.
     * Extracts payment context from token and populates Auth + routeTo.
     *
     * Flow:
     * 1. Call GET /tokens/payment-token/extract with token in header
     * 2. Extract returns: { OrganizationId, CustomerId, ContractIds[], AccessToken, RefreshToken, Email, expiresAt }
     * 3. Populate Auth from extracted data
     * 4. Set routeTo with Payment and contract IDs
     * 5. Return modified config with Auth populated
     */
    async initializeFromPaymentToken(config: ConsumerPortalConfig, paymentToken: string): Promise<ConsumerPortalConfig> {
        try {
            // Step 1: Call extraction endpoint
            const extractUrl = 'tokens/payment-token/extract';
            const apiQuery: ApiQuery = {
                headers: {
                    /* eslint-disable @typescript-eslint/naming-convention */
                    Authorization: paymentToken
                    /* eslint-enable @typescript-eslint/naming-convention */
                }
            };

            const response = await this.consumerPortalApi.get<PaymentTokenExtractResponse>(extractUrl, apiQuery).toPromise();

            // consumerPortalApi.get() extracts result.Records which can be object or array
            const extracted = (Array.isArray(response) ? response[0] : response) as PaymentTokenExtractResponse;

            if (!extracted || !extracted.accessToken || !extracted.organizationId) {
                throw new Error(`Payment token extraction failed: Invalid response data - ${JSON.stringify(response)}`);
            }

            // Step 2: Build modified config with Auth populated
            const modifiedConfig: ConsumerPortalConfig = {
                ...config,
                organizationId: extracted.organizationId,
                /* eslint-disable @typescript-eslint/naming-convention */
                Auth: {
                    accessToken: extracted.accessToken,
                    RefreshToken: extracted.refreshToken || undefined,
                    Email: extracted.email
                },
                /* eslint-enable @typescript-eslint/naming-convention */
                routeTo: {
                    route: ComponentsToLoad.Payment,
                    routeParams: {
                        contractIds: extracted.contractIds,
                        contractCustomerId: extracted.customerId,
                        amount: extracted.amount
                    }
                }
            };

            return modifiedConfig;
        } catch (error) {
            // Log actual error for troubleshooting (don't mask it)
            this.logger.logError(error as Error);
            // Re-throw original error to preserve stack trace and message
            throw error;
        }
    }

    consolidateConsumerPortalConfig(cpConfig?: string, cpAppConfig?: ConsumerPortalConfig): ConsumerPortalConfig {
        let consolidatedInputs: ConsumerPortalConfig = {
            ...consumerPortalDefaults,
            ...cpAppConfig
        };
        if (cpConfig && !isEmpty(cpConfig)) {
            const inputConfig = JSON.parse(cpConfig) as ConsumerPortalConfig;
            consolidatedInputs = {
                ...consolidatedInputs,
                ...inputConfig
            };
        }
        /**
         * Set enabled Features
         */
        this.featureToggleService.setFeatures(
            this.featureToggleService.mergeFeatures(consumerPortalDefaults?.enabledFeatures, consolidatedInputs?.enabledFeatures),
            consolidatedInputs?.organizationType
        );
        this.setGlobalHiddenFeatures(consolidatedInputs?.hiddenFeatures ?? []);
        this.config = consolidatedInputs;
        this.consumerPortalApi.setConsumerPortalApiRoute(consolidatedInputs.apiUrl);
        this.siteSetting.setStylingForSite(consolidatedInputs);
        this.loginConfig = JSON.stringify({
            ...this.inputs,
            apiUrl: consolidatedInputs?.apiUrl,
            styling: consolidatedInputs?.styling,
            logoUrl: consolidatedInputs?.header?.logoUrl,
            faviconUrl: consolidatedInputs?.header?.faviconUrl,
            storeName: consolidatedInputs?.header?.title,
            depotName: consolidatedInputs?.header?.depotName,
            organizationId: !isEmpty(consolidatedInputs.organizationId) ? consolidatedInputs.organizationId : this.inputs?.organizationId,
            license: consolidatedInputs?.licVersion,
            loginApiUrl: consolidatedInputs?.loginApiUrl,
            consumerPortalApiUrl: consolidatedInputs?.apiUrl,
            privacyURL: this.getPrivacyTermsLink(consolidatedInputs?.organizationType as OrganizationType, consolidatedInputs.privacyURL)
        });
        // Change the favicon icon dynamically
        if (this.faviconEl && consolidatedInputs?.header?.faviconUrl) {
            this.faviconEl.href = consolidatedInputs?.header?.faviconUrl;
        }
        // Change the title dynamically
        if (this.title && consolidatedInputs?.header?.title) {
            this.title.setTitle(consolidatedInputs?.header?.title);
        }

        return consolidatedInputs;
    }

    saveOrganizationConfiguration(organizationConfig: OrganizationConfig) {
        const orgId = this.adminInputs.value.organizationId;
        return this.consumerPortalApi.patchOrganizationConfiguration(orgId, organizationConfig).pipe(
            switchMap(c => {
                this.orgConfiguration.next((c as OrganizationConfig) || {});
                return of(c);
            }),
            catchError(error => {
                this.logger.logError(error);
                this.logger.alertDevError(error);
                this.errorService.setError(ErrorType.InvalidSubDomainOrgId);
                return of(this.orgConfiguration.value);
            })
        );
    }

    setMatIconFontFamily() {
        let head = document.getElementsByTagName('head')[0];
        let link = document.createElement('link');

        const cssId = 'mat-icon'; // you could encode the css path itself to generate id..
        if (!document.getElementById(cssId)) {
            head = document.getElementsByTagName('head')[0];
            link = document.createElement('link');
            link.id = cssId;
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            link.media = 'all';
            head.appendChild(link);
        }

        const roboto = 'roboto-font'; // you could encode the css path itself to generate id..
        if (!document.getElementById(roboto)) {
            head = document.getElementsByTagName('head')[0];
            link = document.createElement('link');
            link.id = roboto;
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = 'https://fonts.googleapis.com/css2?family=Open+Sans&family=Roboto&display=swap';
            link.media = 'all';
            head.appendChild(link);
        }

        const monstrret = 'monstrret'; // you could encode the css path itself to generate id..
        if (!document.getElementById(roboto)) {
            head = document.getElementsByTagName('head')[0];
            link = document.createElement('link');
            link.id = monstrret;
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400&display=swap';
            link.media = 'all';
            head.appendChild(link);
        }
    }

    setInputsConfig(inputs: ConsumerPortalConfig | undefined) {
        this.inputs = inputs;
    }

    getPrivacyTermsLink(organizationType?: OrganizationType, privacyURL?: string): string | undefined {
        if (!this.featureToggleService.isAvailable('enabledPrivacyPolicy')) {
            return;
        }

        if (privacyURL && privacyURL.length > 0) {
            return privacyURL;
        }

        const baseUrl = 'https://www.point-of-rental.com';

        switch (organizationType) {
            case OrganizationType.SYRINX:
                return `${baseUrl}/eu-privacy-policy`;
            default:
                return `${baseUrl}/privacy-policy`;
        }
    }

    /**
     * These are manually hidden features in DB by POR - overrides all
     * @param hiddenFeatures
     */
    setGlobalHiddenFeatures(hiddenFeatures: string[]) {
        this.featureToggleService.setHiddenFeatures([
            hiddenFeatures?.includes('SecureUrlLoginRequired') ? 'secureUrlLoginRequired' : '',
            hiddenFeatures?.includes('CallOffAbility') ? 'callOffAbility' : '',
            hiddenFeatures?.includes('TrackingTab') ? 'trackingTab' : '',
            hiddenFeatures?.includes('RequestServiceAbility') ? 'requestService' : '',
            hiddenFeatures?.includes('MultiplePaymentAbility') ? 'multiplePaymentAbility' : '',
            hiddenFeatures?.includes('QuotesPaymentAbility') ? 'quotesPaymentAbility' : '',
            hiddenFeatures?.includes('EnabledPrivacyPolicy') ? 'enabledPrivacyPolicy' : '',
            hiddenFeatures?.includes('LiveTracking') ? 'liveTracking' : '',
            hiddenFeatures?.includes('AssetTags') || hiddenFeatures?.includes('AsssetTags') ? 'assetTags' : '',
            hiddenFeatures?.includes('AutoConvertQuotes') || hiddenFeatures?.includes('AutoConvertQuotes') ? 'autoConvertQuotes' : ''
        ]);
    }
}
